export const redactedValue = '********';

/**
 * Property-name denylist for PII / secrets. Matched case-insensitively as a
 * substring of the key (e.g. `birth` matches `dateOfBirth`, `token` matches
 * `accessToken`). Used by analytics and PostHog network scrubbing.
 *
 * Fairlo-relevant keys only (API/UI property names), plus a small secret set
 * needed because a custom `maskCapturedNetworkRequestFn` replaces PostHog's
 * automatic body redaction. PostHog still always denies sensitive headers in
 * the SDK — this list does not mirror PostHog's full payload deny-list.
 */
export const sensitiveDataKeys = [
  // Identity / contact
  'email',
  'phone',
  'mobile',
  'firstName',
  'lastName',
  'givenName',
  'fullName',
  'userName',
  'businessName',
  'businessId',
  // National ID / DOB (`birth` also matches `dateOfBirth`)
  'personalNumber',
  'nationalNumber',
  'birth',
  // Address
  'address',
  'postalCode',
  'postCode',
  'town',
  'city',
  'street',
  'buildingNumber',
  'buildingName',
  'county',
  // Bank / payment identifiers
  'accountNumber',
  'sortCode',
  'clearingNumber',
  'paymentReference',
  'referenceNumber',
  // Secrets that appear in Fairlo auth payloads / URLs.
  // `token` is a substring match (covers accessToken / refreshToken / idToken / authToken)
  // and is also used by GTM and trackEvent property masking.
  'password',
  'token',
  'authorization',
];

const sensitiveDataRegExp = new RegExp(sensitiveDataKeys.join('|'), 'i');

export const isSensitiveDataKey = (key: string): boolean => sensitiveDataRegExp.test(key);

type MaskedObject<ObjectType extends Record<string, unknown>> = {
  [Key in keyof ObjectType]: ObjectType[Key] extends Record<string, unknown>
    ? MaskedObject<ObjectType[Key]> | string
    : ObjectType[Key] | string;
};

/**
 * Masks sensitive data in an object by replacing values of keys that match a sensitive data pattern with '********'.
 * If a value is an object or array, the function is called recursively to mask sensitive data within nested structures.
 * Arrays are preserved as arrays.
 *
 * @example
 * maskSensitiveData({
 *  user: { email: 'john.doe@mail.com' },
 *  feedback: 'lorem ipsum',
 * })
 * -> { user: { email : '********' }, feedback: 'lorem ipsum' }
 */
export const maskSensitiveData = <T extends Record<string, unknown>>(object: T) => {
  const filteredObject: MaskedObject<T> = { ...object };

  for (const key in filteredObject) {
    if (sensitiveDataRegExp.test(key)) {
      filteredObject[key] = redactedValue;
    } else if (Array.isArray(filteredObject[key])) {
      // Preserve arrays and recursively mask items within them
      // @ts-expect-error - Typescript won today
      filteredObject[key] = (filteredObject[key] as unknown[]).map(item => {
        if (typeof item === 'object' && item !== null) {
          return maskSensitiveData(item as Record<string, unknown>);
        }
        return item;
      });
    } else if (typeof filteredObject[key] === 'object' && filteredObject[key] !== null) {
      // @ts-expect-error - Typescript won today
      filteredObject[key] = maskSensitiveData(filteredObject[key] as Record<string, unknown>);
    }
  }

  return filteredObject;
};
